import { useMutation } from '@tanstack/react-query';
import { queryClient, QUERY_KEYS } from '@src/query';
import { ApiError } from '@src/services/api';
import { ApiTypes } from '@src/../common/dist';
import { updateMediaFindData } from './useQueryMediaFind';
import { updateObjectLabels } from './api';
import { MediaView } from 'common';

// This delay is quite conservative since we don't really need to apply the update
// right away; the user focus is probably not there anyway
const productGroupUpdateDelayMillis = 3000;

interface QuerySnapshot {
  queryKey: readonly unknown[];
  data: unknown;
}

interface MediaObjectLabelsUpdateContext {
  snapshots: QuerySnapshot[];
}

function getMediaQuerySnapshots(mediaIds: string[]): QuerySnapshot[] {
  const snapshots: QuerySnapshot[] = [];
  const seenQueryKeys = new Set<string>();

  for (const mediaId of new Set(mediaIds)) {
    const mediaQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEYS.media(mediaId) });
    for (const query of mediaQueries) {
      const queryKey = query.queryKey;
      const serializedQueryKey = JSON.stringify(queryKey);
      if (!seenQueryKeys.has(serializedQueryKey)) {
        seenQueryKeys.add(serializedQueryKey);
        snapshots.push({ queryKey, data: query.state.data });
      }
    }
  }

  const mediaFindQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEYS.mediaFind() });
  for (const query of mediaFindQueries) {
    const queryKey = query.queryKey;
    const serializedQueryKey = JSON.stringify(queryKey);
    if (!seenQueryKeys.has(serializedQueryKey)) {
      seenQueryKeys.add(serializedQueryKey);
      snapshots.push({ queryKey, data: query.state.data });
    }
  }

  return snapshots;
}

// Subtitle tracks are matched by the media key prefix but are an array: skip them.
function isMediaViewCacheData(data: unknown): data is MediaView {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
}

function updateMediaQueryCaches(
  mediaId: string,
  productIdsExact?: string[],
  productIdsSimilar?: string[],
): void {
  const mediaQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEYS.media(mediaId) });
  for (const query of mediaQueries) {
    queryClient.setQueryData<MediaView | undefined>(query.queryKey, (oldData) => {
      if (!isMediaViewCacheData(oldData)) {
        return oldData;
      }

      return {
        ...oldData,
        productIdsExact,
        productIdsSimilar,
      };
    });
  }
}

export const useMutationMediaObjectLabelsUpdate = () => useMutation<void, ApiError, ApiTypes.ParamsPutObjectLabels, MediaObjectLabelsUpdateContext>({
  mutationFn: (params) => updateObjectLabels(params),
  onMutate: async ({ medias }) => {
    const mediaIds = medias.map(({ mediaId }) => mediaId);
    await Promise.all([
      ...[...new Set(mediaIds)].map((mediaId) =>
        queryClient.cancelQueries({ queryKey: QUERY_KEYS.media(mediaId) })
      ),
      // updateMediaFindData touches every mediaFind variant, so cancel all of
      // them before taking the rollback snapshot and applying the update.
      queryClient.cancelQueries({ queryKey: QUERY_KEYS.mediaFind() }),
    ]);

    const context: MediaObjectLabelsUpdateContext = {
      snapshots: getMediaQuerySnapshots(mediaIds),
    };

    for (const { mediaId, productIdsExact, productIdsSimilar } of medias) {
      updateMediaFindData(mediaId, {
        productIdsExact,
        productIdsSimilar,
      });
      updateMediaQueryCaches(mediaId, productIdsExact, productIdsSimilar);
    }

    return context;
  },
  onError: (_error, _variables, context) => {
    context?.snapshots.forEach(({ queryKey, data }) => {
      queryClient.setQueryData(queryKey, data);
    });
  },
  onSuccess: (_, { medias }) => {
    medias.forEach(({
      mediaId,
      productIdsExact,
      productIdsSimilar,
    }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collectionsMedia() });

      updateMediaFindData(mediaId, {
        productIdsExact,
        productIdsSimilar,
      });

      // Keep every media detail variant in sync, including queries carrying a
      // transcription language suffix.
      updateMediaQueryCaches(mediaId, productIdsExact, productIdsSimilar);
    });

    // Unfortunately, the update to the product groups happens by a fire-and-forget action from web-app
    // The refreshing delay reflects the time it takes for the update send by web-app to be processed by catalong
    // The media detail and media-find caches are updated optimistically above;
    // only product groups still need the delayed refresh.
    setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.productGroups()] });
    }, productGroupUpdateDelayMillis);
  },
});
